import { PROMO_CODES } from '../../config/promo.js';
import { state, set, subscribe } from '../../state/store.js';
import { applyDiscountCode } from '../../lib/shopify.js';
import { isPhone } from '../../lib/validate.js';
import { formatPhoneE164 } from '../../lib/format.js';

export function initPromo() {
  const input = document.getElementById('promo-input');
  const btn   = document.getElementById('promo-apply');
  const msg   = document.getElementById('promo-msg');
  if (!input || !btn || !msg) return;

  // When the cart updates with an active code (e.g. on initial hydration or
  // after a successful apply), reflect it in the input and message area.
  function syncFromCart() {
    const active = (state.cart?.discountCodes || []).find((d) => d.applicable);
    if (active) {
      if (input.value.toUpperCase() !== active.code.toUpperCase()) input.value = active.code;
      const amt = state.cart.discount || 0;
      msg.className = 'promo-msg ok';
      msg.textContent = amt > 0
        ? `Промокод застосовано: -${amt.toFixed(2)} ${state.cart.currency || ''}`.trim()
        : 'Промокод застосовано.';
    }
  }
  syncFromCart();
  subscribe(['cart'], syncFromCart);

  btn.addEventListener('click', async () => {
    const code = input.value.trim().toUpperCase();
    if (!code) {
      msg.className = 'promo-msg err';
      msg.textContent = 'Введіть промокод';
      return;
    }

    // Phone-bound validation: a valid phone is required so Shopify can
    // check the code against the customer record (per-customer limits,
    // customer-segment eligibility, etc.). If the customer hasn't filled
    // the phone yet, refuse and point them at it.
    const phone   = state.contact?.phone;
    const country = state.contact?.phoneCountry;
    if (!phone || !isPhone(phone, country)) {
      msg.className = 'promo-msg err';
      msg.textContent = 'Спочатку введіть номер телефону, щоб перевірити промокод';
      const phoneInput = document.querySelector('#contact-fields input[name="phone"]');
      phoneInput?.focus({ preventScroll: false });
      phoneInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const e164Phone = formatPhoneE164(phone, country);

    const cartId = state.cart?.id;
    const shop   = state.shop?.shopifyDomain;
    btn.disabled = true;
    msg.className = 'promo-msg';
    msg.textContent = 'Перевірка…';

    // Live path: Shopify validates and applies the code, returns updated cart.
    // The phone is sent so the server can attach it as buyerIdentity on the
    // cart BEFORE applying the code — Shopify then evaluates per-customer
    // limits / customer-eligibility against that identity.
    if (cartId && shop) {
      try {
        const result = await applyDiscountCode({ shop, cartId, code, phone: e164Phone });
        if (result?.cart) {
          // Replace cart state so totals reflect the discount immediately.
          set({ cart: { ...state.cart, ...result.cart } });
        }
        msg.className = 'promo-msg ' + (result?.applied ? 'ok' : 'err');
        msg.textContent = result?.message || (result?.applied ? 'Промокод застосовано.' : 'Не вдалося застосувати.');
        set({ promo: result?.applied ? { code, message: result.message } : null });
      } catch (err) {
        msg.className = 'promo-msg err';
        // Server-side errors are already in Ukrainian; only fall back when
        // the message looks like raw English (network / parse failure).
        const raw = String(err.message || '').trim();
        msg.textContent = /^[А-ЯҐЄІЇа-яґєії]/.test(raw)
          ? raw
          : 'Не вдалося застосувати промокод. Спробуйте ще раз.';
      } finally {
        btn.disabled = false;
      }
      return;
    }

    // Offline/dev fallback: use the static map. Useful when running without
    // a Shopify cart token (e.g. on the test URL with fallback data).
    const found = PROMO_CODES[code];
    if (found) {
      set({ promo: { code, ...found } });
      msg.className = 'promo-msg ok';
      msg.textContent = `Промокод застосовано: ${found.label}`;
    } else {
      set({ promo: null });
      msg.className = 'promo-msg err';
      msg.textContent = 'Недійсний промокод';
    }
    btn.disabled = false;
  });
}
